import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getAudit, type AuditEvent, type AuditQuery } from '@/lib/api';

/**
 * The agency's audit log (SPEC §8; AUD-3, AUD-5).
 *
 * **The first accumulating hook in this directory**, and the difference matters:
 * every other one holds a whole answer, so a refetch replaces it. A log pages
 * forward with a cursor, so `loadMore` appends and a filter change has to throw
 * the accumulation away — the cursor belongs to the predicate that produced it,
 * and carrying it forward would splice two different queries together.
 *
 * **It takes no agency.** AUD-3 gives an admin their own partition and the API
 * resolves it from the token; the client never names one (SEC-5). And it is not
 * authorization: `AdminOnly` decides what a non-admin is *told*, while the API
 * refuses every non-admin request regardless (AUD-7).
 */

export interface AuditFilters {
  /** AUD-5's event class. `all` is sent as nothing — the API defaults to it. */
  eventClass: 'all' | 'agency' | 'counterpart';
  spaceId?: string;
  /** Matched exactly and case-insensitively server-side, never as a fragment. */
  actorEmail?: string;
}

export type AuditState =
  | { kind: 'loading' }
  /** An empty log is a loaded state — a filter matching nothing is an answer. */
  | { kind: 'loaded'; events: AuditEvent[]; hasMore: boolean; loadingMore: boolean }
  /** `status` carried because 403 and 500 differ to the reader. `0` is network-level. */
  | { kind: 'error'; status: number };

export interface UseAuditResult {
  state: AuditState;
  /** After a failure. Clears back to `loading` first. */
  retry: () => void;
  /** Fetch the next page and append it. A no-op unless `hasMore`. */
  loadMore: () => void;
}

/**
 * How many requests one load will chain through **empty** pages.
 *
 * A page with no rows and a token means "nothing matched in what was scanned,
 * keep going" — the server reads a bounded number of rows per request, and a
 * selective filter can match none of them (ADR-0015). Without this, a filter
 * with few matches gives an empty screen and a button that visibly does
 * nothing. With no bound at all, one click could walk the entire log.
 */
const MAX_CHAINED_FETCHES = 5;

interface Fetched {
  events: AuditEvent[];
  cursor: string | undefined;
}

async function fetchPage(
  query: AuditQuery,
  from: string | undefined,
  signal: AbortSignal,
): Promise<Fetched> {
  const events: AuditEvent[] = [];
  let cursor = from;

  for (let attempt = 0; attempt < MAX_CHAINED_FETCHES; attempt += 1) {
    const body = await getAudit(
      { ...query, ...(cursor === undefined ? {} : { continuationToken: cursor }) },
      signal,
    );
    events.push(...body.events);
    cursor = body.continuationToken;
    if (events.length > 0 || cursor === undefined) break;
  }

  return { events, cursor };
}

function queryFrom(filters: AuditFilters): AuditQuery {
  return {
    eventClass: filters.eventClass,
    ...(filters.spaceId === undefined ? {} : { spaceId: filters.spaceId }),
    ...(filters.actorEmail === undefined ? {} : { actorEmail: filters.actorEmail }),
  };
}

export function useAudit(filters: AuditFilters): UseAuditResult {
  // The effect keys on the filter **fields**, not the object. A caller that
  // builds `{ eventClass }` inline would otherwise refetch on every render.
  const key = `${filters.eventClass}\0${filters.spaceId ?? ''}\0${filters.actorEmail ?? ''}`;

  const [state, setState] = useState<AuditState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [appliedKey, setAppliedKey] = useState(key);

  const cursorRef = useRef<string | undefined>(undefined);
  const generation = `${key}#${String(reloadToken)}`;
  const generationRef = useRef(generation);

  // Adjusting state during render rather than in an effect — React's own
  // pattern for "a prop changed and the state derived from it is now stale".
  // The effect below would work too, but it would render the old page once
  // before blanking it, which is precisely the flicker `refresh` elsewhere in
  // this directory exists to avoid.
  if (key !== appliedKey) {
    setAppliedKey(key);
    setState({ kind: 'loading' });
  }

  useEffect(() => {
    const controller = new AbortController();
    generationRef.current = generation;
    cursorRef.current = undefined;

    fetchPage(queryFrom(filters), undefined, controller.signal)
      .then(({ events, cursor }) => {
        cursorRef.current = cursor;
        setState({ kind: 'loaded', events, hasMore: cursor !== undefined, loadingMore: false });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
    // `filters` is read inside, but `generation` is what decides when to refetch
    // — it is those same fields, flattened, plus the retry counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation]);

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (cursor === undefined) return;

    const requested = generation;
    setState((current) =>
      current.kind === 'loaded' ? { ...current, loadingMore: true } : current,
    );

    const controller = new AbortController();
    fetchPage(queryFrom(filters), cursor, controller.signal)
      .then(({ events, cursor: next }) => {
        // A filter changed while this was in flight. Appending now would splice
        // the old predicate's rows into the new one's list — the one race the
        // non-accumulating hooks in this directory never had to handle.
        if (generationRef.current !== requested) return;

        cursorRef.current = next;
        setState((current) =>
          current.kind === 'loaded'
            ? {
                kind: 'loaded',
                events: [...current.events, ...events],
                hasMore: next !== undefined,
                loadingMore: false,
              }
            : current,
        );
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        if (generationRef.current !== requested) return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });
    // Same reason as the effect: the fields, not the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation]);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, loadMore };
}
